import { Segmented } from '../../components/Controls.jsx';
import { Trash } from '../../lib/icons.tsx';
import { mediaKey } from '../../../shared/media-keys.ts';

/**
 * "Describe your images" — the wizard's answer to a photo landing on the wrong
 * slide.
 *
 * The build agent receives uploads as a list of filenames, so `IMG_4821.jpg`
 * tells it nothing and it places the photo by guesswork. This step asks the one
 * question the user can actually answer before the deck exists: what is this a
 * picture OF. It deliberately does NOT ask them to map images onto slides —
 * before the build there are no slides to map onto, and asking someone to name
 * a slide they have never seen makes them do the deck's job twice.
 *
 * TWO KINDS OF PICTURE reach this list, and the difference matters to the
 * author, not to the build:
 *  - what they UPLOADED, which nothing knows anything about;
 *  - what came OUT OF their deck (a rebuild), which already knows the slide it
 *    was on, so its description is a correction rather than an introduction —
 *    and whose remove button is the only way to say "leave that one behind",
 *    since they never had the file to withhold.
 *
 * Everything here is optional and nothing gates Build. An untouched form sends
 * no notes at all, which is byte-identical to how the wizard behaved before this
 * existed — so the cost of ignoring the step is exactly zero.
 *
 * OPT-IN, and off by default. It used to open itself whenever there were
 * pictures and photography was on, which put a form on screen in front of anyone
 * who dropped in a PDF with two photos in it — paperwork before they had asked
 * for any. The wizard's resting state is a plain thumbnail grid; the switch
 * above it opens this.
 *
 * When it does open it REPLACES that grid rather than sitting under it: showing
 * every picture twice, once to look at and once to describe, reads as two lists
 * that have to be kept in step. The grid carries the same items and the same
 * remove behaviour, so nothing is only reachable from in here — which was the
 * other half of the problem, since binning an extracted picture is the one way
 * to say "leave that one behind" and it used to cost a trip through this form.
 */

/** Identity for a picked file. Byte-identical to `dedupe()`'s key in
 *  NewDeckModal — the two must agree, or removing and re-adding an image
 *  strands its description under a key nothing looks up. */
export function noteKey(file: { name: string; size: number }): string {
  return file.name + ':' + file.size;
}

/** Identity for a picture that came out of an uploaded document. Prefixed so it
 *  can never collide with an upload whose filename happens to match, and built
 *  on the wire key so the two cannot drift — the upload id is what tells two
 *  documents' `slide-01-image1.jpg` apart (`shared/media-keys.ts`). */
export function sourceKey(uploadId: string | undefined, name: string): string {
  return 'src:' + mediaKey(uploadId, name);
}

/** What a picture is FOR — the question the Photography switch used to answer
 *  by accident. Dropping images in used to flip that switch from stock to
 *  "use my uploads", so every upload was deck imagery whether it was a
 *  photograph for a slide or a rate card sent for context (Jake, 2026-08-10).
 *  The switch is now only about stock; this is where a picture is held back. */
const USES = [
  { value: 'deck', label: 'In the deck', title: 'Place it on the slide it suits' },
  { value: 'reference', label: 'Reference only', title: 'Read it for content, never put it on a slide' },
];

const WEIGHTS = [
  { value: 'hero', label: 'Feature it', title: 'Give it the deck’s most prominent image treatment' },
  { value: 'normal', label: 'Use it', title: 'Place it wherever it fits the content best' },
  { value: 'optional', label: 'If it fits', title: 'Only use it if a slide genuinely suits it' },
];

const isClip = (name: string) => /\.(mp4|webm|mov)$/i.test(name);

/** One picture in the describe step. The two sources are told apart by
 *  `source`: a picture the author uploaded carries its `File`, one extracted
 *  from their deck does not and is keyed by name instead. */
export type NoteItem = {
  key: string;
  name: string;
  url: string;
  /** True for a picture that came OUT of an uploaded document. It already has a
   *  home, so its question is what it shows, not where it goes. */
  source?: boolean;
  /** The wire key for a source picture — what the bin records and what a note is
   *  sent under. Held on the item so this component never has to know an upload
   *  id exists. */
  mediaKey?: string;
  /** Which document it came out of, as the author knows that file. Only used
   *  when more than one contributed pictures. */
  docName?: string;
  page?: number;
  file?: File;
};

/** What the author said about one picture. `use` is 'deck' by default and is
 *  the only field that changes whether the picture appears at all. */
export type ImageNote = { caption?: string; section?: string; weight?: string; use?: string };

export default function ImageNotes({
  items,
  headings = [],
  notes,
  onChange,
  onRemove,
}: {
  items: NoteItem[];
  /** The brief's own section headings, offered as "belongs with" choices. */
  headings?: string[];
  notes: Record<string, ImageNote>;
  onChange: (next: Record<string, ImageNote>) => void;
  onRemove?: (item: NoteItem) => void;
}) {
  // A dropdown offering one destination isn't a choice. Below two headings the
  // row is hidden entirely rather than shown empty — a title-only build has no
  // brief to draw sections from, and that is a normal way to use the wizard.
  const showSections = headings.length >= 2;
  const fromDeck = items.filter((i) => i.source);
  const added = items.filter((i) => !i.source);
  // Document order, preserved: `items` arrives grouped by document already, and
  // a Map keeps first-seen order, so the headings read in the order the author
  // dropped the files.
  const docGroups = [
    ...fromDeck
      .reduce((acc, i) => {
        const k = i.docName || 'your deck';
        return acc.set(k, [...(acc.get(k) || []), i]);
      }, new Map<string, NoteItem[]>())
      .entries(),
  ];

  function set(key: string, field: keyof ImageNote, value: string) {
    const prev = notes[key] || {};
    onChange({ ...notes, [key]: { ...prev, [field]: value } });
  }

  function row(item: NoteItem) {
    const note = notes[item.key] || {};
    const clip = isClip(item.name);
    // A reference picture is not being placed, so the two questions about
    // placement have no answer to give and are hidden rather than shown inert.
    const reference = note.use === 'reference';
    return (
      <li key={item.key} className="img-notes__row">
        <div className="img-notes__media" title={item.name}>
          {clip ? <video src={item.url} muted loop playsInline /> : <img src={item.url} alt="" />}
        </div>

        <div className="img-notes__fields">
          <label className="img-notes__field">
            <span className="img-notes__label">
              {/* A picture from the deck already has a home, so the question is
                  what it IS, not where it goes. */}
              What it shows
              {item.page ? <em className="img-notes__from"> — from slide {item.page}</em> : null}
            </span>
            <input
              type="text"
              className="modal__input"
              maxLength={300}
              value={note.caption || ''}
              placeholder={clip ? 'e.g. the espresso machine running' : 'e.g. Manchester site aerial'}
              onChange={(e) => set(item.key, 'caption', e.target.value)}
            />
          </label>

          <div className="img-notes__field img-notes__field--weight">
            <span className="img-notes__label">What it’s for</span>
            <Segmented
              label={`What ${item.name} is for`}
              value={note.use || 'deck'}
              onChange={(v) => set(item.key, 'use', v)}
              options={USES}
            />
          </div>

          {showSections && !reference && (
            <label className="img-notes__field img-notes__field--section">
              <span className="img-notes__label">Belongs with</span>
              <select
                className="modal__input"
                value={note.section || ''}
                onChange={(e) => set(item.key, 'section', e.target.value)}
              >
                <option value="">{item.source ? 'The slide it came from' : 'Anywhere it fits'}</option>
                {headings.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </label>
          )}

          {!reference && (
            <div className="img-notes__field img-notes__field--weight">
              <span className="img-notes__label">How much</span>
              <Segmented
                label={`How prominently to use ${item.name}`}
                value={note.weight || 'normal'}
                onChange={(v) => set(item.key, 'weight', v)}
                options={WEIGHTS}
              />
            </div>
          )}
        </div>

        <button
          type="button"
          className="iconbtn img-notes__remove"
          onClick={() => onRemove?.(item)}
          aria-label={item.source ? `Leave out ${item.name}` : `Remove ${item.name}`}
        >
          <Trash />
        </button>
      </li>
    );
  }

  return (
    <div className="img-notes">
      <p className="img-notes__intro muted small">
        Tell us what these show and we’ll put each one with the right slide. Skip it and we’ll work it out. Anything you
        mark <strong>reference only</strong> is read for its content and never placed on a slide.
      </p>

      {/* One group per document once there is more than one, because "from your
          deck" cannot answer "which one" — and with two briefs open the author
          is looking at two `slide-01-image1.jpg`s that differ only by which
          heading they sit under. A single document keeps the sentence it had. */}
      {docGroups.map(([docName, group]) => (
        <div key={docName}>
          <h4 className="img-notes__group">{docGroups.length > 1 ? `From ${docName}` : 'From your deck'}</h4>
          <p className="img-notes__hint muted small">
            These came out of the file you uploaded. Each one goes back on the slide it came from unless you say
            otherwise, and the bin leaves it out altogether.
          </p>
          <ul className="img-notes__list" role="list">
            {group.map(row)}
          </ul>
        </div>
      ))}

      {added.length > 0 && (
        <>
          {fromDeck.length > 0 && <h4 className="img-notes__group">You added</h4>}
          <ul className="img-notes__list" role="list">
            {added.map(row)}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Notes → the `imageNotes` the intake accepts, uploads first then the deck's
 * own pictures, each in its own order.
 *
 * Two things are dropped here rather than downstream: the neutral 'normal'
 * weight (absence already means "place it normally", so sending it spends
 * launch-prompt budget saying nothing), and any entry left completely blank.
 *
 * Keyed by raw `File.name` for an upload — the server owns the rewrite to the
 * bundle path, because only the server knows what it called the file after
 * sanitising it. A picture out of a DOCUMENT is keyed by the upload it came out
 * of as well as its name: the server minted that name from the page number
 * alone, so two documents both hand back a `slide-01-image1.jpg` and the name on
 * its own cannot say which one a caption is about (`shared/media-keys.ts`).
 */
/** One entry of the wizard's `imageNotes` payload. */
export type SerialisedImageNote = {
  file: string;
  caption?: string;
  section?: string;
  weight?: string;
  /** Only ever 'reference'. 'deck' is the default and says nothing, so it is
   *  dropped here like the neutral weight. */
  use?: 'reference';
};

export function serialiseImageNotes(
  images: File[],
  notes: Record<string, ImageNote>,
  sourceMedia: { uploadId?: string; name: string }[] = [],
): SerialisedImageNote[] {
  const out: SerialisedImageNote[] = [];
  const push = (file: string, n: ImageNote | undefined) => {
    if (!n) return;
    const caption = (n.caption || '').trim();
    const reference = n.use === 'reference';
    // Both placement fields are meaningless on a picture that is not being
    // placed, and the form hides them — so they are dropped rather than sent as
    // a stale answer from before the author changed their mind.
    const section = reference ? '' : (n.section || '').trim();
    const weight = !reference && n.weight && n.weight !== 'normal' ? n.weight : null;
    if (!caption && !section && !weight && !reference) return;
    out.push({
      file,
      ...(caption ? { caption } : {}),
      ...(section ? { section } : {}),
      ...(weight ? { weight } : {}),
      ...(reference ? { use: 'reference' as const } : {}),
    });
  };
  for (const f of images) push(f.name, notes[noteKey(f)]);
  for (const m of sourceMedia) push(mediaKey(m.uploadId, m.name), notes[sourceKey(m.uploadId, m.name)]);
  return out;
}
